// ─────────────────────────────────────────────────────────────────────────────
//  SEED CATALOG — the Tecniqa product range bundled with the app.
//
//  Why this exists:
//  Products normally live in localStorage (written by the admin panel), which
//  means a fresh browser sees an empty storefront. This file gives the site the
//  full catalogue out of the box so every section renders.
//
//  Shape note: entries below match EXACTLY what `saveProduct` / `saveCategory`
//  in `utils/storage.js` produce. Nothing downstream can tell a seed record
//  from an admin-created one, so no page needs special-casing.
//
//  Imagery: `productArt` is generated artwork keyed by slug — see
//  `tools/generate-catalog-art.mjs`. Each entry is [studio, contrast, technical].
//  Swap in real photography by replacing the SVGs under assets/catalog/products
//  (or by pointing `images` at your own files); nothing else needs to change.
//
//  Replacing this with a backend: delete this file and rewrite the read
//  functions in `utils/storage.js` to call the API. No page imports this
//  module directly — `utils/storage.js` is the only consumer.
// ─────────────────────────────────────────────────────────────────────────────

import { productArt } from "../assets/catalog";

// Seed ids carry this prefix so `storage.js` can tell them apart from
// admin-created records (which use a `${Date.now()}_${rand}` id).
export const SEED_PREFIX = "seed-";

export const isSeedId = (id) => String(id ?? "").startsWith(SEED_PREFIX);

const SEEDED_AT = "2026-01-15T09:00:00.000Z";

// Build the HTML shape Quill produces, since CategoryDetails parses <li> items.
const bullets = (items) => `<ul>${items.map((i) => `<li>${i}</li>`).join("")}</ul>`;

// ─── CATEGORIES ──────────────────────────────────────────────────────────────
export const seedCategories = [
  {
    id: "seed-cat-hvac-tools",
    name: "HVAC Tools",
    slug: "hvac-tools",
    eyebrow: "Field Ready",
    description:
      "Vacuum pumps, manifold gauges, leak detectors, recovery machines and the hand tools an AC service technician carries to every call.",
    isActive: true,
    createdAt: SEEDED_AT,
  },
  {
    id: "seed-cat-copper-tubes",
    name: "Copper Tubes",
    slug: "copper-tubes",
    eyebrow: "Premium Grade",
    description:
      "Premium soft copper coils and hard drawn straight pipe in 3/16\" through 7/8\" and other sizes, in a full range of wall thicknesses.",
    isActive: true,
    createdAt: SEEDED_AT,
  },
  {
    id: "seed-cat-refrigerant-gas",
    name: "Refrigerant Gas",
    slug: "refrigerant-gas",
    eyebrow: "Cylinder Range",
    description:
      "Refrigerant cylinders in every common capacity — R-22, R-134A, R-32, R-410A, R-407A, R-124A and R-227EA — sourced from reputed manufacturers.",
    isActive: true,
    createdAt: SEEDED_AT,
  },
  {
    id: "seed-cat-gas-cans",
    name: "Refrigerant Gas Cans",
    slug: "refrigerant-gas-cans",
    eyebrow: "Small Pack",
    description:
      "Small-pack refrigerant cans in R-290, R-600, R-134A and R-22 for top-ups, domestic refrigeration and single-unit service work.",
    isActive: true,
    createdAt: SEEDED_AT,
  },
  {
    id: "seed-cat-stabilizers",
    name: "Stabilizers",
    slug: "stabilizers",
    eyebrow: "Power Protection",
    description:
      "Air conditioner voltage stabilizers from 0.5 KVA to 5 KVA with wide 90V–290V input correction, suitable for every type of AC system.",
    isActive: true,
    createdAt: SEEDED_AT,
  },
  {
    id: "seed-cat-compressors",
    name: "Compressors",
    slug: "compressors",
    eyebrow: "Core Component",
    description:
      "Rotary, scroll and reciprocating compressors across the common capacities, including GMCC, Highly, Rechi, Emerson, Sanyo and Danfoss builds.",
    isActive: true,
    createdAt: SEEDED_AT,
  },
  {
    id: "seed-cat-indoor-outdoor-units",
    name: "Indoor & Outdoor Units",
    slug: "indoor-outdoor-units",
    eyebrow: "Complete Systems",
    description:
      "Separate indoor and outdoor units engineered with precise craftsmanship and advanced technology to maximise system performance.",
    isActive: true,
    createdAt: SEEDED_AT,
  },
];

// ─── PRODUCTS ────────────────────────────────────────────────────────────────
// `defineProduct` fills in the boilerplate every record needs so the entries
// below stay readable and impossible to get structurally wrong.
const defineProduct = ({
  id,
  category,
  name,
  brand,
  badge,
  shortDescription,
  description,
  highlights,
  mrp,
  salePrice,
  availability = "In Stock",
  warranty = "1 Year Manufacturer Warranty",
  art,
  createdAt = SEEDED_AT,
}) => {
  const cat = seedCategories.find((c) => c.id === category);
  const images = productArt[art];

  if (!cat) throw new Error(`Unknown category "${category}" on product "${name}"`);
  if (!images) throw new Error(`No generated art for key "${art}" on product "${name}"`);

  const seoUrl = name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-");

  return {
    id: `${SEED_PREFIX}${id}`,
    categoryId: cat.id,
    categorySlug: cat.slug,
    name,
    brand,
    badge,
    shortDescription,
    description: `<p>${description}</p>`,
    highlights: bullets(highlights),
    mrp,
    salePrice,
    discount: Math.round(((mrp - salePrice) / mrp) * 100),
    availability,
    warranty,
    metaTitle: `${name} | ${brand} | Tecniqa`,
    metaDescription: shortDescription.slice(0, 160),
    seoUrl,
    images,
    isActive: true,
    createdAt,
  };
};

export const seedProducts = [
  // ── HVAC TOOLS ─────────────────────────────────────────────────────────────
  defineProduct({
    id: "ht-vacuum-pump",
    category: "seed-cat-hvac-tools",
    name: "Vacuum Pump",
    brand: "Value",
    badge: "Best Seller",
    shortDescription:
      "Two-stage rotary vane vacuum pump for deep, fast evacuation on split, ducted and packaged air conditioning systems.",
    description:
      "The workhorse of any AC service kit. A two-stage rotary vane design pulls the system down to a genuine deep vacuum so moisture and non-condensables leave the circuit instead of sitting in it. The oil sight glass and quick-drain valve keep servicing to a two-minute job, and the thermally protected motor handles long pull-downs in hot plant rooms without nuisance tripping.",
    highlights: [
      "Two-stage rotary vane design for deep vacuum",
      "Thermally protected 1/2 HP motor",
      "Large oil sight glass with quick-drain valve",
      "Isolation valve on the intake port",
      "Gas ballast for handling moisture-heavy systems",
    ],
    mrp: 18500,
    salePrice: 14250,
    art: "vacuum-pump",
  }),
  defineProduct({
    id: "ht-manifold-gauge",
    category: "seed-cat-hvac-tools",
    name: "Manifold Gauge",
    brand: "Camipro",
    badge: "Best Seller",
    shortDescription:
      "Twin-gauge brass manifold with colour-coded hoses, calibrated for R-22, R-134A, R-410A and R-407C service work.",
    description:
      "A forged brass body with a piston-type valve mechanism that holds a seal far longer than a plated alternative. Both gauge faces carry pressure-temperature scales for the common refrigerants, so you can read saturation temperature straight off the dial while charging instead of reaching for a chart.",
    highlights: [
      "Forged brass body with piston-type valves",
      "Dual scales for R-22, R-134A, R-410A and R-407C",
      "Shock-resistant gauge housings with sight glass",
      "Three colour-coded 1/4\" SAE charging hoses included",
      "Hanging hook for single-handed use on site",
    ],
    mrp: 4200,
    salePrice: 2950,
    art: "manifold-gauge",
  }),
  defineProduct({
    id: "ht-multimeter",
    category: "seed-cat-hvac-tools",
    name: "Digital Multimeter",
    brand: "ADN",
    badge: "Trending",
    shortDescription:
      "True-RMS digital multimeter with capacitance and continuity ranges for compressor, capacitor and control-circuit fault finding.",
    description:
      "Covers what AC electrical work actually needs. The capacitance range checks a run capacitor without pulling it to a bench, continuity with a buzzer traces control wiring quickly, and the True-RMS front end reads correctly on inverter-driven loads where an averaging meter will not.",
    highlights: [
      "True-RMS AC and DC voltage measurement",
      "Capacitance range for run and start capacitors",
      "Resistance, continuity buzzer and diode test",
      "CAT III 600V safety rating with shrouded leads",
      "Backlit display with data hold and auto power-off",
    ],
    mrp: 2400,
    salePrice: 1590,
    art: "digital-multimeter",
  }),
  defineProduct({
    id: "ht-leak-detector",
    category: "seed-cat-hvac-tools",
    name: "Refrigerant Leak Detector",
    brand: "Value",
    badge: "Featured",
    shortDescription:
      "Heated-diode leak detector with a flexible probe and multi-level sensitivity, effective on all common HCFC, HFC and HFO refrigerants.",
    description:
      "Finds the leak instead of confirming there is one. Six sensitivity levels let you sweep a whole coil on the coarse setting, then step down to pinpoint the exact joint. The flexible gooseneck probe reaches behind headers and into service valve pockets where a rigid wand cannot go.",
    highlights: [
      "Detects leaks down to 3 g/yr",
      "Six selectable sensitivity levels",
      "Works with HCFC, HFC and HFO refrigerants",
      "Flexible gooseneck probe for awkward joints",
      "Replaceable heated-diode sensor cell",
    ],
    mrp: 9800,
    salePrice: 7250,
    art: "leak-detector",
  }),
  defineProduct({
    id: "ht-recovery-machine",
    category: "seed-cat-hvac-tools",
    name: "Refrigerant Recovery Machine",
    brand: "Value",
    badge: "Best Seller",
    shortDescription:
      "Portable oil-less recovery machine handling liquid, vapour and push-pull recovery on all common CFC, HCFC and HFC refrigerants.",
    description:
      "An oil-less compressor means there is no oil to check between jobs and no risk of carrying contamination from one system into the next. High and low pressure cut-outs protect the unit automatically, and the whole machine is light enough for one technician to carry up to a rooftop plant.",
    highlights: [
      "Oil-less compressor — no cross-contamination between jobs",
      "Liquid, vapour and push-pull recovery modes",
      "Automatic high-pressure cut-out",
      "Handles CFC, HCFC and HFC refrigerants",
      "Portable build with integrated carry handle",
    ],
    mrp: 68000,
    salePrice: 54500,
    warranty: "2 Year Manufacturer Warranty",
    art: "recovery-machine",
  }),
  defineProduct({
    id: "ht-tube-cutter",
    category: "seed-cat-hvac-tools",
    name: "Tube Cutter",
    brand: "Galaxy",
    badge: "Trending",
    shortDescription:
      "Heavy-duty copper tube cutter with a hardened cutting wheel and built-in reamer, covering 3 mm to 28 mm outside diameter.",
    description:
      "A square, burr-free cut is what makes the flare that follows seal properly. The hardened alloy cutting wheel tracks without wandering, spare wheels store in the knob, and the fold-out reamer deburrs the bore immediately so swarf never makes it into the system.",
    highlights: [
      "Cuts 3 mm to 28 mm (1/8\" to 1-1/8\") copper tube",
      "Hardened alloy cutting wheel with spare in the knob",
      "Fold-out reamer for immediate deburring",
      "Precision rollers keep the cut square",
      "Compact body for work in tight service spaces",
    ],
    mrp: 950,
    salePrice: 620,
    warranty: "6 Month Warranty",
    art: "tube-cutter",
  }),
  defineProduct({
    id: "ht-tube-bender",
    category: "seed-cat-hvac-tools",
    name: "Tube Bender",
    brand: "Galaxy",
    badge: "Featured",
    shortDescription:
      "Lever-type tube bender producing clean 0–180° bends in soft copper without kinking or flattening the tube.",
    description:
      "A supported bend keeps full bore through the turn, which matters more on refrigerant lines than most installers assume — a flattened bend is a permanent restriction. The calibrated angle scale lets you repeat the same bend across a run, and the reverse-bend feature forms offsets in tight plant rooms. Also stocked in the ADN range.",
    highlights: [
      "Clean bends to 180° with no kinking or flattening",
      "Covers 1/4\", 5/16\", 3/8\", 1/2\", 5/8\" and 3/4\" tube",
      "Calibrated angle scale for repeatable bends",
      "Reverse bending for forming offsets",
      "Also available in the ADN range",
    ],
    mrp: 3400,
    salePrice: 2350,
    art: "tube-bender",
  }),
  defineProduct({
    id: "ht-screwdriver",
    category: "seed-cat-hvac-tools",
    name: "Multi-bit Screwdriver",
    brand: "Taparia",
    badge: "New Arrival",
    shortDescription:
      "Multi-bit screwdriver set with chrome vanadium bits and a magnetic quick-change shaft for AC panel and control work.",
    description:
      "One handle covers the whole spread of fasteners on an indoor unit, an outdoor casing and a control board. The magnetic shaft holds the screw while you start it — which is the difference between a five-second job and fishing a dropped screw out of a drain tray. Also stocked in the Multitech range.",
    highlights: [
      "Chrome vanadium bits, hardened and precision ground",
      "Magnetic quick-change shaft holds the screw",
      "Slotted, Phillips, Pozidriv, Torx and hex bits",
      "Soft-grip handle with high-torque profile",
      "Also available in the Multitech range",
    ],
    mrp: 850,
    salePrice: 545,
    warranty: "6 Month Warranty",
    art: "multi-bit-screwdriver",
  }),
  defineProduct({
    id: "ht-jet-pump",
    category: "seed-cat-hvac-tools",
    name: "Jet Pump",
    brand: "Btali",
    badge: "Trending",
    shortDescription:
      "High-pressure jet pump for AC coil and condenser cleaning, supplied with hose, spray gun and adjustable nozzle.",
    description:
      "Built for AC servicing rather than car washing — the pressure is high enough to lift compacted dirt out of a fin pack but controllable enough not to fold the fins over. The induction motor runs continuously through a full day of service calls, and the adjustable nozzle switches from fan spray to a concentrated jet without changing tips.",
    highlights: [
      "High-pressure output tuned for coil cleaning",
      "Continuous-duty induction motor",
      "Supplied with hose, spray gun and adjustable nozzle",
      "Total-stop system cuts the motor when the trigger releases",
      "Also available in the Xtra Power range",
    ],
    mrp: 12500,
    salePrice: 8990,
    art: "jet-pump",
  }),
  defineProduct({
    id: "ht-drill-machine",
    category: "seed-cat-hvac-tools",
    name: "Drill Machine",
    brand: "Bosch",
    badge: "Featured",
    shortDescription:
      "Variable-speed reversible impact drill with a 13 mm keyed chuck, rated for masonry core work on AC installations.",
    description:
      "Sized for installation work: it drills the wall penetration for a line set, the anchor holes for an outdoor unit bracket, and the pilot holes for an indoor mounting plate without changing tools. Variable speed with a lock-on trigger and a reversible motor for backing anchors out cleanly. Also stocked in the Ralli Wolf range.",
    highlights: [
      "13 mm keyed chuck with impact/drill selector",
      "Variable speed trigger with lock-on and reverse",
      "Rated for masonry, concrete, steel and wood",
      "Side handle and depth stop supplied",
      "Also available in the Ralli Wolf range",
    ],
    mrp: 5600,
    salePrice: 3990,
    art: "drill-machine",
  }),

  // ── COPPER TUBES ───────────────────────────────────────────────────────────
  defineProduct({
    id: "cu-soft-coil",
    category: "seed-cat-copper-tubes",
    name: "Soft Copper Tube Coil",
    brand: "Camipro",
    badge: "Best Seller",
    shortDescription:
      "Premium quality soft annealed copper coil for refrigerant line sets, available in 3/16\" through 7/8\" and other sizes.",
    description:
      "Soft annealed, degreased and nitrogen-charged, then capped at both ends so the bore reaches site as clean as it left the mill. Supplied in level-wound coils that pay out without twisting, in every diameter a split, VRF or ducted line set calls for. Also stocked in the MPCL and Indigo ranges.",
    highlights: [
      "Sizes: 3/16\", 1/4\", 5/16\", 3/8\", 1/2\", 5/8\", 3/4\" and 7/8\"",
      "Soft annealed for bending without a former",
      "Degreased, nitrogen-charged and capped both ends",
      "Level-wound coils pay out without twisting",
      "Also available in the MPCL and Indigo ranges",
    ],
    mrp: 4800,
    salePrice: 3850,
    warranty: "Not Applicable",
    art: "copper-tube-coil",
  }),
  defineProduct({
    id: "cu-hard-pipe",
    category: "seed-cat-copper-tubes",
    name: "Hard Copper Straight Pipe",
    brand: "MPCL",
    badge: "Featured",
    shortDescription:
      "Hard drawn straight copper pipe in a full range of diameters and wall thicknesses for fixed refrigeration and chiller pipework.",
    description:
      "Hard drawn pipe holds its line across a long run without the sag a soft coil develops, which is what you want on an exposed plant-room riser or a chilled water header. Supplied in standard straight lengths across the common diameters, with the wall thickness selected to suit the operating pressure.",
    highlights: [
      "Full range of diameters and wall thicknesses",
      "Hard drawn — holds a straight run without sagging",
      "Suitable for high-pressure R-410A and R-32 systems",
      "Standard straight lengths, capped and clean bore",
      "Also available in the Camipro and Indigo ranges",
    ],
    mrp: 2900,
    salePrice: 2280,
    warranty: "Not Applicable",
    art: "copper-straight-pipe",
  }),

  // ── REFRIGERANT GAS ────────────────────────────────────────────────────────
  defineProduct({
    id: "rg-r22",
    category: "seed-cat-refrigerant-gas",
    name: "R-22 Refrigerant Cylinder",
    brand: "Floron",
    badge: "Best Seller",
    shortDescription:
      "High purity R-22 (F-22) refrigerant in a refillable cylinder, for servicing existing HCFC air conditioning and refrigeration plant.",
    description:
      "The long-standing service refrigerant for older split, packaged and cold-room plant. Supplied at high purity with a moisture content held low enough that a correctly evacuated system stays dry after charging. Cylinders are refillable, valve-protected and delivered with a batch test certificate.",
    highlights: [
      "High purity R-22 (also sold as F-22)",
      "Refillable cylinder with protective valve collar",
      "Low moisture content, batch tested",
      "For servicing existing HCFC systems",
      "Available in multiple cylinder capacities",
    ],
    mrp: 9500,
    salePrice: 7650,
    warranty: "Not Applicable",
    art: "gas-r22",
  }),
  defineProduct({
    id: "rg-r134a",
    category: "seed-cat-refrigerant-gas",
    name: "R-134A Refrigerant Cylinder",
    brand: "Camipro",
    badge: "Trending",
    shortDescription:
      "High purity R-134A refrigerant cylinder for chillers, automotive air conditioning and medium-temperature refrigeration.",
    description:
      "A single-component HFC, so it charges as liquid or vapour without the fractionation risk a blend carries — which makes it forgiving on top-ups. Standard fill for centrifugal chillers, bus and car air conditioning and medium-temperature commercial refrigeration.",
    highlights: [
      "Single component — no fractionation on top-up",
      "Zero ozone depletion potential",
      "Suits chillers, automotive AC and medium-temp refrigeration",
      "Refillable cylinder with protective valve collar",
      "Available in multiple cylinder capacities",
    ],
    mrp: 11800,
    salePrice: 9400,
    warranty: "Not Applicable",
    art: "gas-r134a",
  }),
  defineProduct({
    id: "rg-r32",
    category: "seed-cat-refrigerant-gas",
    name: "R-32 Refrigerant Cylinder",
    brand: "MPCL",
    badge: "Trending",
    shortDescription:
      "High purity R-32 refrigerant cylinder for modern inverter split systems, with lower GWP and higher volumetric capacity than R-410A.",
    description:
      "R-32 is the standard charge on current inverter splits: a single component with roughly a third of the GWP of R-410A and a higher volumetric capacity, so a system needs materially less refrigerant for the same duty. It is an A2L mildly flammable refrigerant — handle, store and charge to the appropriate safety practice.",
    highlights: [
      "Single component — charges without fractionation",
      "Around one third the GWP of R-410A",
      "Higher volumetric capacity means a smaller charge",
      "Standard fill for modern inverter split systems",
      "A2L classified — follow mildly flammable handling practice",
    ],
    mrp: 9200,
    salePrice: 7350,
    warranty: "Not Applicable",
    art: "gas-r32",
  }),
  defineProduct({
    id: "rg-r410a",
    category: "seed-cat-refrigerant-gas",
    name: "R-410A Refrigerant Cylinder",
    brand: "Godrej",
    badge: "Best Seller",
    shortDescription:
      "High purity R-410A refrigerant cylinder for split, ducted and VRF air conditioning systems designed around high-pressure operation.",
    description:
      "The high-pressure HFC blend most split and VRF equipment of the last two decades was built for. Being a near-azeotropic blend it must be charged as liquid to keep the composition right — vapour charging will shift the mix and the performance with it. Supplied with a dip-tube cylinder to make that straightforward.",
    highlights: [
      "Near-azeotropic blend — charge as liquid",
      "For split, ducted, cassette and VRF systems",
      "Zero ozone depletion potential",
      "Dip-tube cylinder for liquid charging",
      "Available in multiple cylinder capacities",
    ],
    mrp: 14500,
    salePrice: 11800,
    warranty: "Not Applicable",
    art: "gas-r410a",
  }),
  defineProduct({
    id: "rg-r407a",
    category: "seed-cat-refrigerant-gas",
    name: "R-407A Refrigerant Cylinder",
    brand: "Floron",
    badge: "Featured",
    shortDescription:
      "High purity R-407A refrigerant cylinder, a lower-GWP retrofit route for R-22 medium and low temperature refrigeration.",
    description:
      "A zeotropic HFC blend used to retrofit R-22 commercial refrigeration — cold rooms, display cases and process cooling — without redesigning the plant. Because it has a temperature glide it must be charged as liquid, and a leak should be recovered and recharged rather than topped up.",
    highlights: [
      "Retrofit route for R-22 refrigeration plant",
      "Zeotropic blend with temperature glide — charge as liquid",
      "Suits medium and low temperature applications",
      "Zero ozone depletion potential",
      "Refillable cylinder with protective valve collar",
    ],
    mrp: 13200,
    salePrice: 10600,
    warranty: "Not Applicable",
    art: "gas-r407a",
  }),
  defineProduct({
    id: "rg-r124a",
    category: "seed-cat-refrigerant-gas",
    name: "R-124A Refrigerant Cylinder",
    brand: "SRF",
    badge: "Featured",
    shortDescription:
      "High purity R-124A refrigerant cylinder for specialist medium-pressure chiller and industrial process cooling duties.",
    description:
      "A specialist HCFC used on medium-pressure centrifugal chillers and certain industrial process cooling duties, and as a component in several service blends. Supplied at high purity with a batch certificate; stocked to order in the larger capacities.",
    highlights: [
      "High purity, batch tested with certificate",
      "For medium-pressure chiller and process cooling duties",
      "Also used as a component in service blends",
      "Refillable cylinder with protective valve collar",
      "Larger capacities stocked to order",
    ],
    mrp: 16500,
    salePrice: 13900,
    availability: "Limited Stock",
    warranty: "Not Applicable",
    art: "gas-r124a",
  }),
  defineProduct({
    id: "rg-r227ea",
    category: "seed-cat-refrigerant-gas",
    name: "R-227EA Refrigerant Cylinder",
    brand: "Stallion",
    badge: "Industrial Grade",
    shortDescription:
      "High purity R-227EA (heptafluoropropane) cylinder for clean-agent fire suppression systems and specialist low-temperature duties.",
    description:
      "Best known as the clean agent in fixed fire suppression systems protecting server rooms, switch rooms and control rooms — it leaves no residue and is safe in occupied spaces at design concentration. Also used on specialist low-temperature refrigeration. Supplied at fire-protection grade purity with documentation.",
    highlights: [
      "Fire-protection grade purity with documentation",
      "Clean agent — no residue, safe in occupied spaces",
      "For server rooms, switch rooms and control rooms",
      "Also used on specialist low-temperature duties",
      "Supplied in a valve-protected industrial cylinder",
    ],
    mrp: 42000,
    salePrice: 35500,
    availability: "Limited Stock",
    warranty: "Not Applicable",
    art: "gas-r227ea",
  }),

  // ── REFRIGERANT GAS CANS ───────────────────────────────────────────────────
  defineProduct({
    id: "gc-r290",
    category: "seed-cat-gas-cans",
    name: "R-290 Refrigerant Gas Can",
    brand: "Camipro",
    badge: "New Arrival",
    shortDescription:
      "Small-pack R-290 (propane) refrigerant can for hydrocarbon domestic refrigeration and small commercial cooling.",
    description:
      "Refrigerant-grade propane, dried and purified well beyond fuel grade — ordinary LPG will destroy a sealed system. Now the standard charge on hydrocarbon domestic refrigerators and small commercial units. R-290 is an A3 flammable refrigerant: charge only into equipment designed for it, and follow hydrocarbon handling practice.",
    highlights: [
      "Refrigerant grade, not fuel grade — low moisture and residue",
      "Very low GWP hydrocarbon refrigerant",
      "For hydrocarbon domestic and small commercial systems",
      "Self-sealing can valve, compatible with standard tap adaptors",
      "A3 flammable — charge only into equipment designed for R-290",
    ],
    mrp: 1450,
    salePrice: 995,
    warranty: "Not Applicable",
    art: "can-r290",
  }),
  defineProduct({
    id: "gc-r600",
    category: "seed-cat-gas-cans",
    name: "R-600 Refrigerant Gas Can",
    brand: "Camipro",
    badge: "Trending",
    shortDescription:
      "Small-pack R-600 / R-600a (isobutane) refrigerant can for domestic refrigerator and deep freezer service work.",
    description:
      "The charge in the large majority of domestic refrigerators built in the last decade. Because the charge is small, purity and dryness matter more than volume — this is refrigerant grade, dried and filtered so a correctly evacuated system stays dry. A3 flammable: charge only into equipment designed for hydrocarbons.",
    highlights: [
      "Refrigerant grade isobutane, dried and filtered",
      "Standard charge for domestic refrigerators and freezers",
      "Very low GWP hydrocarbon refrigerant",
      "Self-sealing can valve for controlled small charges",
      "A3 flammable — follow hydrocarbon handling practice",
    ],
    mrp: 1350,
    salePrice: 940,
    warranty: "Not Applicable",
    art: "can-r600",
  }),
  defineProduct({
    id: "gc-r134a",
    category: "seed-cat-gas-cans",
    name: "R-134A Refrigerant Gas Can",
    brand: "Floron",
    badge: "Best Seller",
    shortDescription:
      "Small-pack R-134A refrigerant can for automotive air conditioning, water coolers and single-unit service top-ups.",
    description:
      "The convenient pack size when the job needs a few hundred grams and rolling a 12 kg cylinder to it makes no sense — car AC, water coolers, visi-coolers and bottle coolers. Same high purity R-134A as the cylinder range, with a self-sealing valve so a part-used can stores safely.",
    highlights: [
      "Same high purity R-134A as the cylinder range",
      "Right pack size for automotive AC and water coolers",
      "Self-sealing valve — a part-used can stores safely",
      "Compatible with standard can tap adaptors",
      "Zero ozone depletion potential",
    ],
    mrp: 1100,
    salePrice: 780,
    warranty: "Not Applicable",
    art: "can-r134a",
  }),
  defineProduct({
    id: "gc-r22",
    category: "seed-cat-gas-cans",
    name: "R-22 Refrigerant Gas Can",
    brand: "MPCL",
    badge: "Featured",
    shortDescription:
      "Small-pack R-22 refrigerant can for topping up window units, older splits and small commercial refrigeration.",
    description:
      "For the service call where a window unit or an older split needs a small top-up and a full cylinder is more refrigerant than the job will ever use. Same high purity R-22 as the cylinder range, in a self-sealing can that a technician can carry on a two-wheeler.",
    highlights: [
      "Same high purity R-22 as the cylinder range",
      "Pack size suited to window units and older splits",
      "Self-sealing valve — a part-used can stores safely",
      "Compatible with standard can tap adaptors",
      "Low moisture content, batch tested",
    ],
    mrp: 980,
    salePrice: 690,
    warranty: "Not Applicable",
    art: "can-r22",
  }),

  // ── STABILIZERS ────────────────────────────────────────────────────────────
  defineProduct({
    id: "st-1kva",
    category: "seed-cat-stabilizers",
    name: "Air Conditioner Stabilizer 1 KVA",
    brand: "Camipro",
    badge: "Trending",
    shortDescription:
      "1 KVA air conditioner voltage stabilizer with wide 90V–290V input correction and time-delay compressor protection.",
    description:
      "Sized for a 1 ton split or window unit. The wide input window holds a usable 230V output through the voltage swings a typical Indian supply delivers, and the built-in time delay stops the compressor restarting into a still-pressurised system after a power cut — which is what actually kills compressors on an unprotected supply.",
    highlights: [
      "Suits air conditioners up to 1 ton",
      "Wide 90V–290V input correction range",
      "Time-delay circuit protects the compressor on restart",
      "Over and under voltage cut-off with auto recovery",
      "Wall-mountable ABS enclosure with LED indication",
    ],
    mrp: 3200,
    salePrice: 2290,
    warranty: "2 Year Manufacturer Warranty",
    art: "stabilizer-1kva",
  }),
  defineProduct({
    id: "st-3kva",
    category: "seed-cat-stabilizers",
    name: "Air Conditioner Stabilizer 3 KVA",
    brand: "Voltas",
    badge: "Best Seller",
    shortDescription:
      "3 KVA air conditioner voltage stabilizer for 1.5 to 2 ton systems, with digital display and compressor time delay.",
    description:
      "The size most 1.5 and 2 ton installations call for. A digital display shows input and output voltage side by side, so diagnosing a supply complaint takes a glance rather than a multimeter. Copper windings and a generously rated toroidal core keep it running cool through a full summer duty cycle.",
    highlights: [
      "Suits air conditioners from 1.5 to 2 ton",
      "Digital display of input and output voltage",
      "Wide 90V–290V input correction range",
      "Copper windings on a generously rated core",
      "Time-delay, over-voltage and under-voltage protection",
    ],
    mrp: 5400,
    salePrice: 4150,
    warranty: "2 Year Manufacturer Warranty",
    art: "stabilizer-3kva",
  }),
  defineProduct({
    id: "st-5kva",
    category: "seed-cat-stabilizers",
    name: "Air Conditioner Stabilizer 5 KVA",
    brand: "Microtek",
    badge: "Featured",
    shortDescription:
      "5 KVA heavy-duty voltage stabilizer for larger split, ducted and cassette air conditioning systems.",
    description:
      "For 3 ton and larger indoor units, ducted systems and light commercial installations where the connected load is beyond what a domestic stabilizer will carry. The heavier core and copper windings hold correction accuracy at full load rather than drifting once the unit warms up, and the enclosure is vented for continuous operation.",
    highlights: [
      "Suits larger split, ducted and cassette systems",
      "Holds correction accuracy at full rated load",
      "Wide 90V–290V input correction range",
      "Vented enclosure rated for continuous operation",
      "Time-delay, over-voltage and under-voltage protection",
    ],
    mrp: 8600,
    salePrice: 6790,
    warranty: "2 Year Manufacturer Warranty",
    art: "stabilizer-5kva",
  }),

  // ── COMPRESSORS ────────────────────────────────────────────────────────────
  defineProduct({
    id: "cp-rotary",
    category: "seed-cat-compressors",
    name: "Rotary Compressor",
    brand: "Camipro",
    badge: "Best Seller",
    shortDescription:
      "Hermetic rotary compressor for split and window air conditioners, available across the common capacities in GMCC, Highly and Rechi builds.",
    description:
      "The standard compressor on split and window units up to about 2 ton. A rolling-piston rotary runs with fewer moving parts and less vibration than an equivalent reciprocating unit, which is why it dominates residential air conditioning. Supplied with the correct oil charge and refrigerant designation for the system it is replacing.",
    highlights: [
      "Hermetic rolling-piston rotary design",
      "GMCC, Highly and Rechi builds available",
      "Available across the common residential capacities",
      "Supplied for R-22, R-410A or R-32 as required",
      "Correct oil charge filled and sealed from the factory",
    ],
    mrp: 14500,
    salePrice: 11200,
    art: "compressor-rotary",
  }),
  defineProduct({
    id: "cp-scroll",
    category: "seed-cat-compressors",
    name: "Scroll Compressor",
    brand: "Copeland",
    badge: "Featured",
    shortDescription:
      "Hermetic scroll compressor for ducted, cassette, VRF and chiller applications, in Copeland and Emerson builds.",
    description:
      "Continuous compression across the scroll set means far less pressure pulsation and a flatter efficiency curve at part load than a reciprocating machine — which is why light commercial and chiller plant standardised on it. Supplied with the terminal box, mounting kit and oil charge matched to the application.",
    highlights: [
      "Hermetic scroll design — low pulsation, quiet running",
      "Copeland and Emerson builds available",
      "For ducted, cassette, VRF and chiller applications",
      "Holds efficiency at part load",
      "Terminal box, mounting kit and oil charge supplied",
    ],
    mrp: 48000,
    salePrice: 39500,
    availability: "Limited Stock",
    warranty: "2 Year Manufacturer Warranty",
    art: "compressor-scroll",
  }),
  defineProduct({
    id: "cp-recip",
    category: "seed-cat-compressors",
    name: "Reciprocating Compressor",
    brand: "Godrej",
    badge: "Trending",
    shortDescription:
      "Hermetic reciprocating compressor for refrigeration, cold rooms and deep freezers, in Emerson, Sanyo and Danfoss builds.",
    description:
      "Still the right choice on low-temperature refrigeration, where a recip pulls a deeper suction and tolerates a wider evaporating range than a rotary will. Standard on cold rooms, deep freezers, visi-coolers and bottle coolers. Supplied with the correct oil and refrigerant designation for the duty.",
    highlights: [
      "Hermetic reciprocating design for low-temperature duty",
      "Emerson, Sanyo and Danfoss builds available",
      "For cold rooms, deep freezers and commercial refrigeration",
      "Tolerates a wide evaporating temperature range",
      "Also available in the MPCL range",
    ],
    mrp: 9800,
    salePrice: 7450,
    art: "compressor-recip",
  }),

  // ── INDOOR & OUTDOOR UNITS ─────────────────────────────────────────────────
  defineProduct({
    id: "sy-indoor",
    category: "seed-cat-indoor-outdoor-units",
    name: "Split AC Indoor Unit",
    brand: "Camipro",
    badge: "Featured",
    shortDescription:
      "Wall-mounted split air conditioner indoor unit engineered for quiet operation and even air throw, supplied separately from the outdoor unit.",
    description:
      "Sold as a separate indoor unit so a damaged or aged fan coil can be replaced without changing out a perfectly serviceable condenser — and so an installer can pair capacities to suit the room rather than the box. Precise fin and blower geometry keeps the throw even across the room at low fan speed, where a poorly matched unit gets noisy. Also available in Godrej, Whirlpool, Voltas and Blue Star.",
    highlights: [
      "Supplied separately — pair with any matching outdoor unit",
      "Quiet cross-flow blower with even air throw",
      "Washable anti-dust filter as standard",
      "Available across the common residential capacities",
      "Also available in Godrej, Whirlpool, Voltas and Blue Star",
    ],
    mrp: 22500,
    salePrice: 17900,
    art: "indoor-unit",
  }),
  defineProduct({
    id: "sy-outdoor",
    category: "seed-cat-indoor-outdoor-units",
    name: "Split AC Outdoor Unit",
    brand: "Voltas",
    badge: "Best Seller",
    shortDescription:
      "Split air conditioner outdoor condensing unit with anti-corrosive coated coil, supplied separately from the indoor unit.",
    description:
      "The condensing half of a split system, sold on its own so a failed outdoor unit does not force a full system replacement. The condenser coil carries an anti-corrosive coating, which is what determines service life in coastal and industrial air far more than the compressor does. Also available in Camipro, Godrej, Whirlpool and Blue Star.",
    highlights: [
      "Supplied separately — pair with any matching indoor unit",
      "Anti-corrosive coated condenser coil",
      "Weatherproof powder-coated casing",
      "Available across the common residential capacities",
      "Also available in Camipro, Godrej, Whirlpool and Blue Star",
    ],
    mrp: 34500,
    salePrice: 27900,
    warranty: "1 Year Unit / 5 Year Compressor Warranty",
    art: "outdoor-unit",
  }),
];
